import { registerPlugin, Capacitor } from '@capacitor/core'

/**
 * Мост к нативному VKID SDK (iOS/Android) через Capacitor.
 *
 * Веб-виджет `@vkid/sdk` (OneTap) официально предназначен только для браузера и
 * ненадёжен внутри WebView (PKCE `code_verifier` хранится в cookie — теряется в
 * embedded-WebView). Поэтому на нативных платформах вход идёт через нативные VKID
 * SDK (VKID iOS / com.vk.id Android), обёрнутые Capacitor-плагином `VkIdAuth`.
 *
 * Нативная сторона инициализируется с `client_id`/`client_secret` СВОЕГО приложения
 * (задаются на этапе сборки — Info.plist / manifestPlaceholders, а не приходят с бэка),
 * сама выполняет OAuth 2.1 + PKCE и возвращает сюда токены. Мы отправляем access_token
 * на бэк `/auth/vk/exchange` (с `platform`), где он независимо проверяется у VK.
 */
export interface VkIdNativeAuthResult {
  accessToken: string
  /** id_token (JWT VK ID), если нативный SDK его вернул. */
  idToken?: string
  refreshToken?: string
  userId?: string | number
  deviceId?: string
  expiresIn?: number
}

export interface VkIdNativeAuthorizeOptions {
  /** app_id (client_id) приложения VK ID для текущей платформы/бренда. */
  appId?: string
  /** Список доступов (scope) VK ID, через пробел. */
  scope?: string
}

export interface VkIdAuthPlugin {
  /** Запускает нативный экран авторизации VK ID и возвращает токены. */
  authorize(options: VkIdNativeAuthorizeOptions): Promise<VkIdNativeAuthResult>
  /** Разлогин в нативном SDK (best-effort; на бэк не влияет). */
  logout(): Promise<void>
}

/**
 * Прокси-плагин. Если нативная реализация не зарегистрирована (например, сборка без
 * VKID-модуля) — вызовы отклоняются, и вызывающий код должен откатиться на веб-виджет.
 */
export const VkIdAuth = registerPlugin<VkIdAuthPlugin>('VkIdAuth')

/** Доступна ли нативная реализация VKID на текущей платформе. */
export const isVkIdNativeAvailable = (): boolean => {
  try {
    return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('VkIdAuth')
  } catch {
    return false
  }
}
